'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import ga4 from 'tracking/src/ga4';
import ButtonLink from '../ButtonLink';
import Talent from './_Talent';

interface Props {
  slug: string;
  title: string;
  description: string;
  cta: string;
  talents: {
    name: string;
    image: string;
    role: string;
    yearsOfExperience: number;
    countryFlag: string;
  }[];
}

export default function BannerCta({ slug, title, description, cta, talents }: Props) {
  const sectionRef = useRef<HTMLElement>(null);
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const element = sectionRef.current;
    if (!element || process.env.NEXT_PUBLIC_ANALYTICS_ENABLE === 'false') return () => {};

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedImpression) {
            ga4.event('mid_cta_impression');
            setHasTrackedImpression(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [hasTrackedImpression]);

  const handleCtaClick = (e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (process.env.NEXT_PUBLIC_ANALYTICS_ENABLE === 'false' || !e) return;

    ga4.event('mid_cta_click');
    if (!e.altKey && !e.metaKey && !e.shiftKey && !e.ctrlKey && e.button === 0) {
      e.preventDefault();
      router.push(e.currentTarget.href);
    }
  };

  return (
    <section ref={sectionRef} className="group relative flex flex-col overflow-hidden rounded-xl">
      <div className="bg-site-neutrals-900 mdL:bg-site-neutrals-700 group-hover:mdL:bg-site-neutrals-900 lgS:gap-12 mdS:pr-6 lgS:pl-8 lgS:pr-4 mdS:py-0 mdL:py-0 mdL:pr-3 mdS:grid-cols-[1fr_12.5rem] mdL:grid-cols-[1fr_12.5rem] relative grid grid-cols-1 gap-6 overflow-hidden p-6 pr-8 transition-colors md:grid-cols-1 md:py-6 md:pr-8 2xl:gap-20 2xl:pr-8">
        <div className="mdS:my-auto mdS:py-5 mdL:py-5 z-10 flex flex-col gap-2 md:py-0">
          <span className="site-display-s text-site-neutrals-0 !font-normal">{title}</span>
          <span className="site-paragraph-m text-site-neutrals-50">{description}</span>
        </div>
        <div className="mdS:min-h-[12.25rem] mdL:min-h-[12.25rem] mdL:mx-0 mdS:mx-0 relative -ml-6 -mr-8 md:-ml-6 md:-mr-8 md:min-h-0">
          <div className="mdS:flex-col mdL:flex-col mdS:animate-slide-to-top mdL:animate-slide-to-top md:animate-slide-to-left animate-slide-to-left mdS:absolute mdL:absolute mdS:pt-3 mdL:pt-3 mdS:pl-0 mdL:pl-0 flex w-fit !max-w-none gap-3 pl-3 will-change-transform [animation-duration:100s] md:relative md:flex-row md:pl-3 md:pt-0">
            {[...talents, ...talents].map((talent, index) => (
              <Talent key={index < talents.length ? talent.name : `${talent.name}-2`} {...talent} />
            ))}
          </div>
        </div>
        <div className="from-site-neutrals-900/0 to-site-neutrals-900/100 mdL:bg-gradient-to-b mdL:h-16 mdL:w-full mdS:h-16 mdS:w-full mdS:bg-gradient-to-b absolute bottom-0 right-0 h-full w-8 bg-gradient-to-r md:h-full md:w-8 md:bg-gradient-to-r" />
      </div>
      <div className="bg-site-accent-600 mdL:px-8 px-6">
        <ButtonLink
          href={`https://www.bairesdev.com/start/basic-details/?utm_source=blog&utm_medium=mid-cta&utm_campaign=blog-mid-cta-phase1&utm_content=${slug}`}
          prefetch={false}
          id="banner-cta"
          variant="Tertiary"
          variantColor="White"
          size="Medium"
          iconName="ArrowForward"
          classes="border-none transform-none !text-site-neutrals-0"
          onClick={handleCtaClick}
        >
          {cta}
          <span className="absolute left-0 top-0 z-20 h-full w-full" />
        </ButtonLink>
      </div>
    </section>
  );
}
